import { CartLinesUpdateEvent } from '@shopify/events';

class ProductQuickView extends HTMLElement {
  cache = new Map();

  connectedCallback() {
    this.dialog = this.querySelector('[data-quick-view-dialog]');
    this.content = this.querySelector('[data-quick-view-content]');
    this.loading = this.querySelector('[data-quick-view-loading]');
    this.i18n = {
      loading: this.dataset.i18nLoading || 'Loading product…',
      error: this.dataset.i18nError || 'This product could not be loaded.',
      missing: this.dataset.i18nMissing || 'Quick-view product content is missing',
      addFailed: this.dataset.i18nAddFailed || 'Could not add to cart',
      addedToCart: this.dataset.i18nAddedToCart || 'Added to your cart',
      adding: this.dataset.i18nAdding || 'Adding…',
      added: this.dataset.i18nAdded || 'Added',
      addToCart: this.dataset.i18nAddToCart || 'Add to cart',
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('pointerover', this.onDocumentPointerOver);
    document.addEventListener('submit', this.onDocumentSubmit);
    this.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
    this.dialog?.addEventListener('close', () => this.lastTrigger?.focus());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('pointerover', this.onDocumentPointerOver);
    document.removeEventListener('submit', this.onDocumentSubmit);
  }

  onDocumentClick = (event) => {
    const quickAdd = event.target.closest('[data-product-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      this.quickAdd(quickAdd);
      return;
    }
    const lengthLink = event.target.closest('[data-quick-view-length]');
    if (lengthLink) {
      event.preventDefault();
      this.swap(lengthLink.href);
      return;
    }
    const trigger = event.target.closest('[data-product-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger.dataset.productUrl, trigger);
  };

  onDocumentPointerOver = (event) => {
    const trigger = event.target.closest('[data-product-quick-view]');
    if (trigger?.dataset.productUrl) this.fetchContent(trigger.dataset.productUrl).catch(() => {});
  };

  async open(url, trigger) {
    if (!url || !this.dialog || !this.content || !this.loading) return;
    this.lastTrigger = trigger;
    this.loading.textContent = this.i18n.loading;
    this.loading.hidden = false;
    this.content.hidden = true;
    this.content.replaceChildren();
    if (!this.dialog.open) this.dialog.showModal();

    try {
      const markup = await this.fetchContent(url);
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const productContent = documentFragment.querySelector('[data-product-quick-view-content]');
      if (!productContent) throw new Error(this.i18n.missing);

      this.content.replaceChildren(document.importNode(productContent, true));
      this.loading.hidden = true;
      this.content.hidden = false;
      window.ProductMain?.initAll(this.content);
    } catch (error) {
      this.loading.textContent = this.i18n.error;
      console.error('[product-quick-view]', error);
    }
  }

  // Andere lengte in de quick view: vervang alleen de inhoud, de dialog blijft open.
  async swap(url) {
    const request = (this.swapRequest = (this.swapRequest || 0) + 1);
    try {
      const markup = await this.fetchContent(url);
      if (request !== this.swapRequest) return;
      const productContent = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-product-quick-view-content]');
      if (!productContent) throw new Error(this.i18n.missing);
      this.content.replaceChildren(document.importNode(productContent, true));
      window.ProductMain?.initAll(this.content);
    } catch (error) {
      console.error('[product-quick-view]', error);
    }
  }

  async fetchContent(url) {
    const endpoint = new URL(url, window.location.origin);
    endpoint.searchParams.set('section_id', 'product-quick-view-content');
    const key = endpoint.toString();
    if (!this.cache.has(key)) {
      this.cache.set(key, fetch(key, { headers: { Accept: 'text/html' } }).then((response) => {
        if (!response.ok) throw new Error(`Quick-view request failed: ${response.status}`);
        return response.text();
      }));
    }
    return this.cache.get(key);
  }

  onDocumentSubmit = (event) => {
    const form = event.target.closest('.product-main__form');
    if (!form) return;
    if (event.submitter && !event.submitter.matches('[data-add-button]')) return;
    event.preventDefault();
    this.addToCart(form);
  };

  async requestAdd(variantId, quantity) {
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const payload = { items: [{ id: Number(variantId), quantity }] };
    if (sectionIds.length) payload.sections = sectionIds.join(',');

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || this.i18n.addFailed);
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((item) => item.json());
    return { result, cart };
  }

  async quickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const originalLabel = button.getAttribute('aria-label');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'quick-add',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const { result, cart } = await this.requestAdd(variantId, 1);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'product-cross-sell-quick-add',
          itemCount: 1,
          productId: button.dataset.productId,
          sections: result.sections,
        },
      });
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.setAttribute('aria-label', this.i18n.addedToCart);
      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.removeAttribute('aria-busy');
        button.disabled = false;
        if (originalLabel) button.setAttribute('aria-label', originalLabel);
      }, 1200);
    } catch (error) {
      deferred.reject(error);
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.setAttribute('aria-label', error.message || this.i18n.addFailed);
      window.setTimeout(() => {
        button.classList.remove('is-error');
        if (originalLabel) button.setAttribute('aria-label', originalLabel);
      }, 2200);
    }
  }

  async addToCart(form) {
    const variantId = form.querySelector('[data-variant-id]')?.value;
    const quantity = Math.max(1, Number(form.querySelector('[data-qty-input]')?.value || 1));
    const button = form.querySelector('[data-add-button]');
    const status = form.querySelector('[data-quick-view-status]');
    const productRoot = form.closest('[data-product-section]');
    const isQuickView = productRoot?.hasAttribute('data-product-quick-view-content');
    if (!variantId || !button || button.disabled) return;

    button.disabled = true;
    button.textContent = this.i18n.adding;
    status?.classList.remove('is-error');
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: isQuickView ? 'quick-view' : 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    }));

    try {
      const { result, cart } = await this.requestAdd(variantId, quantity);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: isQuickView ? 'product-quick-view' : 'product-main',
          itemCount: quantity,
          productId: productRoot?.dataset.productId,
          sections: result.sections,
        },
      });
      if (status) status.textContent = this.i18n.addedToCart;
      if (isQuickView && this.dialog.open) {
        this.dialog.close();
      } else {
        const addLabel = form.querySelector('.product-main__buy')?.dataset.addLabel || this.i18n.addToCart;
        button.textContent = this.i18n.added;
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = addLabel;
        }, 1200);
      }
    } catch (error) {
      deferred.reject(error);
      if (status) {
        status.classList.add('is-error');
        status.textContent = error.message || this.i18n.addFailed;
      }
      button.disabled = false;
      button.textContent = form.querySelector('.product-main__buy')?.dataset.addLabel || this.i18n.addToCart;
    }
  }
}

if (!customElements.get('product-quick-view')) customElements.define('product-quick-view', ProductQuickView);
